import type {
  Collection,
  WebflowItem,
  InitUploadResponse,
  CompleteUploadResponse,
} from '../types';

const API_BASE = import.meta.env.VITE_API_BASE || '';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Request failed: ${response.status}`);
    }

    return data;
  }

  async getCollections(): Promise<Collection[]> {
    return this.request<Collection[]>('/api/collections');
  }

  async getItems(
    collectionId: string,
    search: string = ''
  ): Promise<WebflowItem[]> {
    const params = new URLSearchParams({ collectionId, search });
    const data = await this.request<{ items: WebflowItem[] }>(
      `/api/items?${params.toString()}`
    );
    return data.items;
  }

  async initUpload(params: {
    collectionId: string;
    itemId: string;
    fileName: string;
    mimeType: string;
    size: number;
  }): Promise<InitUploadResponse> {
    return this.request<InitUploadResponse>('/api/init-upload', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  }

  async completeUpload(params: {
    collectionId: string;
    itemId: string;
    s3Key: string;
    publicUrl: string;
  }): Promise<CompleteUploadResponse> {
    return this.request<CompleteUploadResponse>('/api/complete-upload', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
}

export const apiClient = new ApiClient(API_BASE);
